import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const CDN = "https://cdn.prod.website-files.com/602f5458a0b1d868c6c84d5b";

const TRUST_LOGOS = [
  { src: `${CDN}/6910cc55979af174280b9f3e_Group%20(2).svg`, alt: "" },
  { src: `${CDN}/6910cd2d43a9203e7ece72e2_g43.svg`, alt: "" },
  { src: `${CDN}/618cf7d27e8255e6af866c7b_Roche.svg`, alt: "Roche", mobileReplace: { src: `${process.env.PUBLIC_URL}/pg-BXHMeVKW.png`, alt: "P&G", noInvert: true } },
  { src: `${CDN}/6910cf070dc7f93e800af8be_Ryanair%201.svg`, alt: "Ryanair", smallMobile: true },
  { src: `${CDN}/6910cf0d2562409136668c19_Publicis_logo%201.svg`, alt: "Publicis" },
  { src: `${CDN}/6910cf14a66ae9bf48ba573d_Schneider_Electric_2007%201.svg`, alt: "Schneider Electric" },
  { src: `${CDN}/64d22435a83397855c43ab85_PMKuwait.svg`, alt: "PM Kuwait" },
  { src: `${CDN}/6910cefaf02d084a2754d157_Group%20(3).svg`, alt: "" },
  { src: `${CDN}/6910cf021d08a6ac28e0d04d_New_York_University_Seal%201.svg`, alt: "NYU" },
  { src: `${process.env.PUBLIC_URL}/pg-BXHMeVKW.png`, alt: "P&G", noInvert: true },
];

const VOLUME_STEPS = ["< 1K", "1K – 10K", "10K – 100K", "100K – 1M", "1M+"];

const PERSONAL_DOMAINS = new Set([
  "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "live.com", "icloud.com",
  "me.com", "mac.com", "aol.com", "protonmail.com", "proton.me", "mail.com",
  "ymail.com", "googlemail.com", "msn.com", "hotmail.co.uk", "yahoo.co.uk",
  "yahoo.in", "rediffmail.com", "zoho.com", "tutanota.com", "fastmail.com",
]);

function isPersonalEmail(email) {
  const domain = email.split("@")[1]?.toLowerCase();
  return domain ? PERSONAL_DOMAINS.has(domain) : false;
}

const empty = { name: "", email: "", company: "", volume: 1, useCase: "", referral: "" };

const inputCls =
  "w-full px-3.5 py-2.5 text-sm text-[#080808] bg-white border border-[#E4E4E7] rounded-lg placeholder:text-[#08080840] focus:outline-none focus:ring-2 focus:ring-[#546FFF] focus:border-transparent transition";

function Field({ label, required, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-[#080808] tracking-wide">
        {label}
        {required && <span className="text-[#546FFF] ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function SuccessState({ onReset }) {
  return (
    <div className="flex flex-col items-center text-center gap-6 py-12">
      <div className="w-16 h-16 rounded-full bg-[#F0F4FF] flex items-center justify-center">
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M8 16l6 6 10-12" stroke="#546FFF" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div className="flex flex-col gap-2">
        <h2 className="text-2xl font-bold text-[#080808]">You&apos;re booked in!</h2>
        <p className="text-[#08080888] text-sm leading-relaxed max-w-[370px]">
          Thanks for reaching out. Someone from our team will email you within{" "}
          <strong className="text-[#080808]">1 business day</strong> to confirm your demo.
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mt-2">
        <a
          href="https://docstrange.nanonets.com/app"
          className="px-5 py-2.5 bg-[#546FFF] text-white text-sm font-semibold rounded-lg hover:bg-[#3A4DB2] transition-colors"
        >
          Explore app →
        </a>
      </div>
    </div>
  );
}

export default function DemoForm() {
  useEffect(() => {
    try {
      if (window.posthog) window.posthog.capture("demo_form_viewed");
    } catch { /* ignore */ }
  }, []);

  const [form, setForm] = useState(empty);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const setField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    if (isPersonalEmail(form.email)) {
      setError("Please use your work email address.");
      setSubmitting(false);
      return;
    }
    try {
      const payload = {
        name: form.name,
        email: form.email,
        company: form.company,
        estimated_monthly_volume: VOLUME_STEPS[form.volume],
        use_case: form.useCase,
        referral: form.referral,
        page: window.location.href,
        type: "demo_request",
      };
      fetch("https://hooks.zapier.com/hooks/catch/26061763/u0j1xsc/", {
        method: "POST",
        body: JSON.stringify(payload),
        mode: "no-cors",
      });
      setSubmitted(true);
      try {
        if (window._hsq) {
          window._hsq.push(["identify", { email: form.email }]);
          window._hsq.push(["trackPageView"]);
        }
        if (window.posthog) {
          window.posthog.capture("demo_form_submitted", { email: form.email });
        }
      } catch { /* analytics errors should never block */ }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* LEFT PANEL */}
      <div className="relative lg:w-[44%] xl:w-[42%] bg-[#080B1A] flex flex-col p-6 md:p-8 lg:p-12 overflow-hidden">
        {/* Background glows */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#546FFF] opacity-[0.12] blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-[#7B93FF] opacity-[0.08] blur-[100px]" />
        </div>

        <div className="relative z-10 flex flex-col lg:flex-1 gap-5 lg:gap-6">
          {/* Logo + back */}
          <div className="flex items-center justify-between">
            <Link to="/">
              <img
                src={`${CDN}/60307e489ca8621554af197d_logo2.webp`}
                alt="Nanonets"
                width={110}
                height={27}
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </Link>
            <Link
              to="/"
              className="text-[#ffffff55] hover:text-white text-sm transition-colors flex items-center gap-1"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M9 11L5 7l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back
            </Link>
          </div>

          {/* Headline */}
          <div className="flex flex-col gap-2">
            <p className="text-[#546FFF] text-xs font-semibold tracking-[0.15em] uppercase">
              Docstrange · Document APIs
            </p>
            <h1 className="text-2xl lg:text-[2rem] font-bold text-white leading-tight">
              See Docstrange in action
            </h1>
            <p className="text-[#ffffff66] text-sm leading-relaxed">
              After you fill out the form, someone from our team will reach out via email. They'll help you schedule a demo where you can see Docstrange live on your own docs, explore use cases, pricing, and a clear path to production.
            </p>
          </div>

          {/* Trust logos — desktop grid */}
          <div className="hidden lg:flex flex-col gap-3 pt-4 flex-1">
            <p className="text-[#546FFF] text-xs font-semibold tracking-[0.15em] uppercase">
              Trusted by leading teams
            </p>
            <div className="grid grid-cols-2 auto-rows-fr flex-1">
              {TRUST_LOGOS.map((logo, i) => (
                <div key={i} className="flex items-center justify-center p-4">
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    style={{
                      height: '28px', width: 'auto', maxWidth: '120px', objectFit: 'contain',
                      opacity: 0.7,
                      filter: logo.noInvert ? undefined : 'brightness(0) invert(1)',
                    }}
                    className="hover:opacity-100 transition-opacity duration-300"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Mobile-only: compact logo grid */}
          <div className="grid lg:hidden grid-cols-3 gap-x-2 gap-y-5 pt-2 pb-1 w-full">
            {TRUST_LOGOS.slice(0, 6).map((logo, i) => {
              const m = logo.mobileReplace || logo;
              return (
                <div key={i} className="flex items-center justify-center">
                  <img
                    src={m.src}
                    alt={m.alt}
                    style={{
                      height: logo.smallMobile ? '14px' : '20px', width: 'auto', maxWidth: '100%', objectFit: 'contain',
                      opacity: 0.6,
                      filter: m.noInvert ? undefined : 'brightness(0) invert(1)',
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom quote — desktop only */}
        <blockquote className="hidden lg:block relative z-10 mt-6 border-l-2 border-[#546FFF] pl-4">
          <p className="text-[#ffffffbb] text-sm leading-relaxed italic">
            Nanonets is our partner in our AI journey with our clients. The value that they have developed has been fundamental in saving cost. They have the technology and we understand maintenance.
          </p>
          <footer className="mt-2 flex items-center gap-2">
            <img
              src="https://cdn.prod.website-files.com/602f5458a0b1d868c6c84d5b/6708fda4d682117fd6e1d233_Neil%20Betts%20.jpeg"
              alt="Neil Betts"
              width={24}
              height={24}
              className="rounded-full"
            />
            <span className="text-[#ffffff55] text-xs">Neil Betts, Co-founder of In<sup>2</sup></span>
          </footer>
        </blockquote>
      </div>

      {/* RIGHT PANEL */}
      <div className="flex-1 bg-white flex items-start justify-center p-6 md:p-10 lg:p-12 overflow-y-auto">
        <div className="w-full max-w-[480px]">
          {submitted ? (
            <SuccessState onReset={() => { setForm(empty); setSubmitted(false); }} />
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-[#080808]">Request a demo</h2>
                <p className="text-[#08080866] text-sm mt-1.5">
                  Fill in your details and we&apos;ll reach out within 1 business day.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <Field label="Name" required>
                  <input type="text" placeholder="Chad Smith" required value={form.name} onChange={setField("name")} className={inputCls} />
                </Field>

                <Field label="Work email" required>
                  <input type="email" placeholder="chad@company.com" required value={form.email} onChange={setField("email")} className={inputCls} />
                </Field>

                <Field label="Company" required>
                  <input type="text" placeholder="Acme Corp" required value={form.company} onChange={setField("company")} className={inputCls} />
                </Field>

                <Field label="Estimated monthly volume">
                  <div className="flex flex-col gap-3 pt-1">
                    <div className="relative">
                      <input
                        type="range"
                        min={0}
                        max={VOLUME_STEPS.length - 1}
                        step={1}
                        value={form.volume}
                        onChange={(e) => setForm((prev) => ({ ...prev, volume: Number(e.target.value) }))}
                        className="volume-slider w-full"
                        style={{ "--val": `${(form.volume / (VOLUME_STEPS.length - 1)) * 100}%` }}
                      />
                    </div>
                    <div className="relative h-4">
                      {VOLUME_STEPS.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, volume: i }))}
                          className={`absolute text-[10px] font-medium transition-colors leading-tight text-center -translate-x-1/2 whitespace-nowrap ${form.volume === i ? "text-[#546FFF]" : "text-[#08080855] hover:text-[#08080888]"}`}
                          style={{ left: `calc(${i / (VOLUME_STEPS.length - 1)} * (100% - 18px) + 9px)` }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#F0F4FF] text-[#546FFF] text-xs font-semibold">
                        {VOLUME_STEPS[form.volume]} documents / month
                      </span>
                    </div>
                  </div>
                </Field>

                <Field label="Tell us about your use case and document types" required>
                  <textarea
                    placeholder="e.g. Analyzing financial reports, summarizing legal cases, extracting data from invoices and POs, implementing RAG on docs, creating document agents for payment and reconciliation"
                    rows={4}
                    required
                    value={form.useCase}
                    onChange={setField("useCase")}
                    className={`${inputCls} resize-none`}
                  />
                </Field>

                <Field label="How did you hear about us?" required>
                  <input type="text" placeholder="e.g. HuggingFace, Hacker News, a colleague…" required value={form.referral} onChange={setField("referral")} className={inputCls} />
                </Field>

                {error && <p className="text-red-500 text-sm">{error}</p>}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3.5 bg-[#546FFF] text-white font-semibold rounded-lg hover:bg-[#3A4DB2] active:scale-[0.99] transition-all duration-150 disabled:opacity-60 text-sm mt-1 cursor-pointer"
                >
                  {submitting ? "Sending…" : "Submit"}
                </button>

                <p className="text-[#08080855] text-xs text-center">
                  By submitting, you agree to our{" "}
                  <a href="https://legal.nanonets.com/privacy" className="underline hover:text-[#546FFF]">Privacy Policy</a>{" "}
                  and{" "}
                  <a href="https://legal.nanonets.com/terms" className="underline hover:text-[#546FFF]">Terms of Service</a>.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
